import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import {
  EntityProps,
  EntityState,
  setAllEntities,
  setEntity,
  updateEntity,
  upsertEntity,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { pipe, switchMap } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { Response } from '../../shared/models/response.model';
import { SelectedEntityState } from '../../shared/store/selected-entity.feature';
import { CartographieService } from '../cartographie.service';
import { Cartographie } from '../models/cartographie.model';
import { CartographieUpdateRequest } from '../request/update-request';
import { CartographieState } from './cartographie.state';

export function avecMéthodesCartographie<_>() {
  return signalStoreFeature(
    {
      state: type<CartographieState & EntityState<Cartographie> & SelectedEntityState>(),
      props: type<EntityProps<Cartographie>>(),
    },
    withMethods((store) => ({
      chargerCartographies: (state: { cartographies: Cartographie[] }) =>
        patchState(store, setAllEntities(state.cartographies)),
      modifierCartographie: (state: { id: string; cartographie: Partial<Cartographie> }) =>
        patchState(store, updateEntity({ id: state.id, changes: state.cartographie })),
      ajouterCartographie: (state: { cartographie: Cartographie }) => patchState(store, setEntity(state.cartographie)),
      upsertCartographie: (state: { cartographie: Cartographie }) =>
        patchState(store, upsertEntity(state.cartographie)),
    })),
    withMethods((store, service = inject(CartographieService)) => ({
      charger: rxMethod<void>(
        pipe(
          switchMap(() =>
            service.findAll().pipe(
              tapResponse({
                next: (result: Response<Cartographie>) => {
                  patchState(store, { paginator: { ...store.paginator(), pageNb: 1 } });
                  store.chargerCartographies({
                    cartographies: result.items,
                  });
                },
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération des cartographies, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      trouverParId: rxMethod<string>(
        pipe(
          switchMap((idCartographie) =>
            service.findById(idCartographie).pipe(
              tapResponse({
                next: (result: Cartographie) => store.upsertCartographie({ cartographie: result }),
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération de la cartographie, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{
        id: string;
        data: CartographieUpdateRequest;
        next?: () => void;
      }>(
        pipe(
          switchMap(({ id, data, next }) =>
            service.update(id, data).pipe(
              switchMap(() => service.findById(id)),
              tapResponse({
                next: (cartographie) => {
                  store.upsertCartographie({ cartographie });
                  next?.();
                },
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la modification de la cartographie, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      dupliquer: rxMethod<{
        id: string;
        nom: string;
        next: () => void;
        finalize: () => void;
      }>(
        pipe(
          switchMap(({ id, nom, next, finalize }) =>
            service.duplicate(id, nom).pipe(
              tapResponse({
                next: (cartographie) => {
                  store.ajouterCartographie({ cartographie });
                  next();
                },
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la duplication de la cartographie, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
                finalize: () => finalize(),
              }),
            ),
          ),
        ),
      ),
      selectionner: (cartographie: Cartographie) => {
        patchState(store, setEntity(cartographie));
        patchState(store, { selectedEntityId: cartographie.id });
      },
      // Tri
      setTri: (nom: string, direction: 'asc' | 'desc') => {
        patchState(store, {
          sort: {
            nom,
            direction: !direction || direction === 'asc' ? '+' : '-',
          },
        });
        // Reset pagination when sort order changes
        patchState(store, { paginator: { ...store.paginator(), pageNb: 1 } });
      },
    })),
  );
}
